import { forwardRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import SelectInfinite from 'Components/Molecules/SelectInfinite/SelectInfinite';
import useInfiniteContacts from 'Hooks/useInfiniteContacts';
import { getContact } from 'Services/invoices';
import { trackError } from 'Utils/errorMonitoring';
import { useTranslation } from 'react-i18next';
import SelectActionButton from 'Components/Atoms/Select/SelectActionButton';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';

const SelectContactLabel = (props) => {
    const { name, integration, tax_id } = props;

    return (
        <div className="items-center grid grid-flow-col w-full grid-cols-[1fr_auto] gap-3">
            <div className="truncate grow">{name}</div>

            <div className="relative">
                {integration && (
                    <img
                        src={integration.icon}
                        alt={integration.name}
                        className="size-3 opacity-50 object-contain grayscale absolute top-1/2 right-full -translate-x-1/2 -translate-y-1/2"
                    />
                )}
                <div className="truncate opacity-50 text-xs ">{tax_id}</div>
            </div>
        </div>
    );
};

SelectContactLabel.propTypes = {
    name: PropTypes.string.isRequired,
    integration: PropTypes.object,
    tax_id: PropTypes.string,
};

const SelectContact = forwardRef(function SelectContact(props, ref) {
    const { className, label, value, onChange, onAdd, error, disabled, ...rest } = props;
    const [displayValue, setDisplayValue] = useState('');
    const [fetchedContact, setFetchedContact] = useState(null);

    const [query, setQuery] = useState('');
    const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteContacts({ query });
    const { t } = useTranslation('contacts');

    const actions = useMemo(() => {
        if (!onAdd) return [];
        return [
            <SelectActionButton icon={<OutlineAdd size={18} />} key="action-add-contact" onClick={() => onAdd()}>
                {t('add-new')}
            </SelectActionButton>,
        ];
    }, [onAdd, t]);

    const options = useMemo(() => {
        const items =
            data?.pages?.reduce((acc, page) => {
                return [...acc, ...page.items];
            }, []) ?? [];

        return items.map((item) => ({
            id: item.id,
            label: (
                <SelectContactLabel
                    key={item.id}
                    name={item.name}
                    integration={item.integration}
                    tax_id={item.tax_id}
                />
            ),
        }));
    }, [data]);

    useEffect(() => {
        if (query) return;

        if (!value || options.length === 0) {
            setDisplayValue('');
            setFetchedContact(null);
            return;
        }

        const matchedOption = options.find((option) => option.id === value);
        if (matchedOption) {
            setDisplayValue(matchedOption.label);
            setFetchedContact(null);
        } else if (!fetchedContact || fetchedContact.id !== value) {
            setDisplayValue('');
            getContact(value)
                .then((contact) => {
                    if (contact) {
                        setDisplayValue(
                            <SelectContactLabel
                                name={contact.name}
                                integration={contact.integration}
                                tax_id={contact.tax_id}
                            />,
                        );
                        setFetchedContact(contact);
                    }
                })
                .catch((error) => {
                    trackError(error);
                });
        }
    }, [value, isFetching, query]);

    return (
        <SelectInfinite
            disabled={disabled}
            ref={ref}
            floatLabel={label}
            label={displayValue}
            value={value}
            onChange={(contactId) => {
                if (!contactId) {
                    onChange(contactId, null);
                } else {
                    const contact = data?.pages?.flatMap((page) => page.items).find((item) => item.id === contactId);
                    onChange(contactId, contact);
                }
            }}
            options={options}
            query={query}
            onChangeQuery={setQuery}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            className={className}
            enableClear={!!displayValue}
            error={error}
            onClear={() => {
                onChange('');
                setQuery('');
            }}
            actions={actions}
            {...rest}
        />
    );
});

export default SelectContact;

SelectContact.propTypes = {
    className: PropTypes.string,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    disabled: PropTypes.bool,
};
